import { useId } from 'react';

/**
 * Role insignia: ribbons and crests.
 *
 * Deliberately *not* the same artwork as a collectible Badge. A Badge is
 * something you earned by playing and it looks like a prize — enamel, foil,
 * a tier ladder. A role is something you were given, and it should read as a
 * mark of office: narrower, quieter, worn beside a name rather than displayed
 * in a case. Two systems that looked identical would tell a player that
 * moderating is a thing you grind for.
 *
 * Same data-driven rule though: `accent`, `motif` and `style` arrive from the
 * server, so a role seeded next year renders on a client built today.
 *
 *   ribbon — Founder, Developer, Artist. Recognition; a hanging pennant.
 *   crest  — Staff, Administrator. Office; a shield.
 */

export interface RoleVisual {
  key: string;
  name: string;
  tag: string;
  accent: string;
  accentDark: string;
  motif: string;
  style: string;
}

/**
 * Motifs at 12x12 — smaller than a Badge's 16x16 because these render at 20px
 * beside a username, where extra detail is just noise. `#` is the motif colour,
 * `+` its highlight.
 */
const MOTIFS: Record<string, readonly string[]> = {
  crown: [
    '............', '.#...##...#.', '###..####..#', '####.####.##',
    '############', '##++++++++##', '##++####++##', '##++++++++##',
    '############', '.##########.', '.##########.', '............',
  ],
  crystal: [
    '.....##.....', '....####....', '...##++##...', '..##++++##..',
    '.##++++++##.', '############', '.##++++++##.', '..##++++##..',
    '...##++##...', '....####....', '.....##.....', '............',
  ],
  shield: [
    '.##########.', '############', '##++++++++##', '##++####++##',
    '##++####++##', '##++++++++##', '##++++++++##', '.##++++++##.',
    '..##++++##..', '...######...', '....####....', '.....##.....',
  ],
  circuit: [
    '..##....##..', '..##....##..', '############', '##++++++++##',
    '##++####++##', '##++#..#++##', '##++#..#++##', '##++####++##',
    '##++++++++##', '############', '..##....##..', '..##....##..',
  ],
  palette: [
    '...######...', '..########..', '.##++##++##.', '##++####++##',
    '##++++++++##', '##++##++++##', '##++++++++##', '##++++##++##',
    '.##++++++##.', '..########..', '...######...', '............',
  ],
  star: [
    '.....##.....', '.....##.....', '....####....', '..########..',
    '############', '.##++++++##.', '..##++++##..', '..########..',
    '.###....###.', '.##......##.', '............', '............',
  ],
};

const BLANK = Array.from({ length: 12 }, () => '.'.repeat(12));

/** Merged runs, same trick as the Badge art — 12x12 is up to 144 rects raw. */
function Motif({ motif, fill, highlight }: { motif: string; fill: string; highlight: string }) {
  const rows = MOTIFS[motif] ?? BLANK;
  const rects: Array<{ x: number; y: number; w: number; colour: string }> = [];

  rows.forEach((row, y) => {
    let start = -1;
    let char = '';
    const flush = (end: number) => {
      if (start < 0) return;
      rects.push({ x: start, y, w: end - start, colour: char === '+' ? highlight : fill });
      start = -1;
      char = '';
    };
    for (let x = 0; x < row.length; x += 1) {
      const cell = row[x]!;
      if (cell === '.') {
        flush(x);
        continue;
      }
      if (cell !== char) {
        flush(x);
        start = x;
        char = cell;
      }
    }
    flush(row.length);
  });

  return (
    <>
      {rects.map((rect, index) => (
        <rect
          key={index}
          x={rect.x}
          y={rect.y}
          width={rect.w}
          height={1}
          fill={rect.colour}
          shapeRendering="crispEdges"
        />
      ))}
    </>
  );
}

/** A pennant with a notched tail, and a shield. Both on a 40x48 canvas. */
const SHAPES: Record<string, string> = {
  ribbon: 'M4 2 H36 V34 L20 26 L4 34 Z',
  crest: 'M4 2 H36 V24 Q36 38 20 46 Q4 38 4 24 Z',
};

export function RoleBadge({
  role,
  size = 34,
  title,
}: {
  role: RoleVisual;
  size?: number;
  title?: string;
}) {
  const uid = useId().replace(/:/g, '');
  const style = SHAPES[role.style] ? role.style : 'ribbon';
  const path = SHAPES[style]!;

  return (
    <svg
      className={`role-badge role-badge--${style} role-badge--${role.key}`}
      viewBox="0 0 40 48"
      width={size}
      height={(size * 48) / 40}
      role="img"
      aria-label={title ?? `${role.name} role`}
      style={{ '--role-accent': role.accent } as React.CSSProperties}
    >
      <defs>
        <linearGradient id={`role-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={role.accent} />
          <stop offset="100%" stopColor={role.accentDark} />
        </linearGradient>
      </defs>

      <path d={path} fill={`url(#role-${uid})`} />
      {/* Ink outline, matching the pixel-frame language the rest of the chrome
          uses — without it these float rather than sitting on the panel. */}
      <path d={path} fill="none" stroke="var(--ink)" strokeWidth="2.5" strokeLinejoin="round" />

      <g transform={`translate(8 ${style === 'crest' ? 8 : 6}) scale(2)`}>
        <Motif motif={role.motif} fill="rgba(0,0,0,.55)" highlight="rgba(255,255,255,.9)" />
      </g>
    </svg>
  );
}

/**
 * The inline tag that sits before a username in chat.
 *
 * Text, not an icon: at chat line-height an emblem is a smudge, and the point
 * of a tag is that it is *read*. The colour does the recognising.
 */
export function RoleTag({ role }: { role: RoleVisual }) {
  return (
    <span
      className="role-tag"
      style={{ '--role-accent': role.accent, '--role-accent-dark': role.accentDark } as React.CSSProperties}
      title={role.name}
    >
      {role.tag}
    </span>
  );
}

/**
 * Every tag a player holds, in priority order.
 *
 * Capped, because somebody with five roles would push their own name off the
 * line. The server already sorts by priority, so the ones that survive the cap
 * are the ones that matter most.
 */
export function RoleTags({ roles, max = 2 }: { roles: RoleVisual[]; max?: number }) {
  if (roles.length === 0) return null;
  return (
    <span className="role-tags">
      {roles.slice(0, max).map((role) => (
        <RoleTag key={role.key} role={role} />
      ))}
    </span>
  );
}
